import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiUrl } from '../../utils/api';
import {
  ChartBarIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

interface ReportData {
  monthlyIncome: Array<{
    month: string;
    income: number;
    expenses: number;
    netIncome: number;
  }>;
  occupancyRate: Array<{
    month: string;
    rate: number;
  }>;
  propertyPerformance: Array<{
    numero: string;
    tipo: string;
    totalIncome: number;
    occupancyDays: number;
    averageRate: number;
  }>;
  yearlySummary: {
    totalIncome: number;
    totalExpenses: number;
    netIncome: number;
    averageOccupancy: number;
    totalReservations: number;
  };
}

const Reports: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState<'monthly' | 'yearly' | 'properties'>('monthly');

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl('/api/dashboard/reports'));
      setReportData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar los reportes');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hotel-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error</h3>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
        <button
          onClick={fetchReportData}
          className="mt-6 btn-primary"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!reportData) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Portal Inversionistas</h1>
        <h2 className="text-2xl font-semibold text-gray-600">Reportes</h2>
        <p className="mt-1 text-sm text-gray-500">
          Análisis detallado de rendimiento financiero y ocupación
        </p>
      </div>

      {/* Selector de tipo de reporte */}
      <div className="card">
        <div className="flex space-x-2">
          <button
            onClick={() => setReportType('monthly')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              reportType === 'monthly'
                ? 'bg-hotel-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <CalendarIcon className="h-4 w-4 inline mr-2" />
            Mensual
          </button>
          <button
            onClick={() => setReportType('yearly')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              reportType === 'yearly'
                ? 'bg-hotel-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <ChartBarIcon className="h-4 w-4 inline mr-2" />
            Anual
          </button>
          <button
            onClick={() => setReportType('properties')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              reportType === 'properties'
                ? 'bg-hotel-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <BuildingOfficeIcon className="h-4 w-4 inline mr-2" />
            Por Propiedades
          </button>
        </div>
      </div>

      {/* Resumen anual */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
              <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Ingresos Totales</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(reportData.yearlySummary.totalIncome)}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-red-100 rounded-lg flex items-center justify-center">
              <CurrencyDollarIcon className="h-5 w-5 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Gastos Totales</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(reportData.yearlySummary.totalExpenses)}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <ChartBarIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Ingreso Neto</p>
              <p className={`text-lg font-semibold ${
                reportData.yearlySummary.netIncome >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatCurrency(reportData.yearlySummary.netIncome)}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <BuildingOfficeIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Ocupación Promedio</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatPercentage(reportData.yearlySummary.averageOccupancy)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos según el tipo de reporte */}
      {reportType === 'monthly' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gráfico de ingresos mensuales */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Ingresos Mensuales</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={reportData.monthlyIncome}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Line type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2} />
                <Line type="monotone" dataKey="expenses" stroke="#EF4444" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Gráfico de ocupación mensual */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tasa de Ocupación Mensual</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={reportData.occupancyRate}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatPercentage(Number(value))} />
                <Bar dataKey="rate" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {reportType === 'properties' && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Rendimiento por Propiedad</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Propiedad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ingresos Totales
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Días Ocupados
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarifa Promedio
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.propertyPerformance.map((property, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {property.numero}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                      {property.tipo}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(property.totalIncome)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {property.occupancyDays} días
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(property.averageRate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Acciones */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Acciones</h3>
        <div className="flex flex-wrap gap-3">
          <button className="btn-primary flex items-center">
            <ChartBarIcon className="h-4 w-4 mr-2" />
            Exportar PDF
          </button>
          <button className="btn-secondary flex items-center">
            <CalendarIcon className="h-4 w-4 mr-2" />
            Programar Reporte
          </button>
        </div>
      </div>
    </div>
  );
};

export default Reports;
